import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowUp } from "lucide-react";

// Floating "back to top" affordance. Rendered globally by AppShell so every
// authenticated route inherits it. Stays hidden until the user has scrolled
// past `SHOW_AFTER_PX` — otherwise it distracts from short pages that don't
// even have anywhere to scroll. Positioned above the mobile bottom-nav pill
// via `bottom-24 md:bottom-6`.
const SHOW_AFTER_PX = 240;

export default function ScrollToTop() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > SHOW_AFTER_PX);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const jumpToTop = () => {
    // Instant reads better than smooth when the page is very long — a smooth
    // scroll from 5000px takes several seconds and feels sluggish. Users who
    // reach for this button want to be at the top NOW.
    window.scrollTo({ top: 0, behavior: "auto" });
  };

  return (
    <button
      type="button"
      onClick={jumpToTop}
      aria-label={t("common.scrollToTop")}
      aria-hidden={!visible}
      tabIndex={visible ? 0 : -1}
      data-testid="scroll-to-top"
      className={`fixed right-3 bottom-24 md:right-4 md:bottom-6 z-40 w-8 h-8 md:w-11 md:h-11 rounded-full bg-white/90 backdrop-blur-md border border-[#E8E5DF] text-leaf-ink shadow-botanical flex items-center justify-center hover:bg-white hover:text-leaf-deep transition-all ${
        visible ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-2 pointer-events-none"
      }`}
    >
      <ArrowUp className="w-4 h-4 md:w-5 md:h-5" strokeWidth={1.5} aria-hidden="true" />
    </button>
  );
}
